import ArticleShell from "@/components/site/ArticleShell";

const BodyEs = () => (
  <div className="article-body">
    <p>Estás en el supermercado. En mitad del pasillo de las frutas. Sin previo aviso, sientes que el corazón se acelera de golpe, el aire no te llega bien, se te nubla la vista. Piensas: <em>"me está pasando algo. ¿Es una recidiva? ¿Es una embolia? ¿Me está dando un infarto?"</em>. Sales corriendo. Tres días después vuelve a pasarte en el coche.</p>
    <p>Bienvenida al mundo de los <strong>ataques de pánico después del cáncer</strong>. No estás volviéndote loca, no estás recayendo y no te estás muriendo. Estás teniendo una respuesta específica de tu sistema nervioso tras un trauma médico sostenido.</p>

    <h2>Qué es un ataque de pánico (y qué no es)</h2>
    <p>Un ataque de pánico es una <strong>respuesta aguda del sistema nervioso simpático</strong> — el mismo que se activa si te asalta un león — solo que sin león visible. Dura entre 5 y 30 minutos, con un pico intenso alrededor del minuto 10. Es <strong>físicamente muy real</strong> pero clínicamente inofensivo: no daña tu cuerpo aunque lo sientas como una catástrofe.</p>
    <p>Síntomas típicos:</p>
    <ul>
      <li>Taquicardia y palpitaciones.</li>
      <li>Sensación de falta de aire, opresión en el pecho.</li>
      <li>Mareo, visión borrosa o "extraña".</li>
      <li>Sudoración, escalofríos o calor.</li>
      <li>Hormigueo en manos o cara.</li>
      <li>Sensación de irrealidad (despersonalización).</li>
      <li>Miedo a "estar muriéndose" o "volverse loca".</li>
    </ul>
    <p>Y lo más importante: <strong>siempre baja</strong>. Nunca ha habido un ataque de pánico que no acabe. El sistema nervioso no puede mantener ese pico más de 30 min.</p>

    <h2>Por qué aparecen en supervivientes de cáncer</h2>
    <p>Tras un proceso oncológico, tu sistema nervioso ha vivido meses o años de amenaza sostenida (diagnóstico, tratamiento, pruebas, incertidumbre). Aunque médicamente ya estés "libre de enfermedad", tu sistema nervioso <strong>guarda memoria</strong> de esa amenaza y puede activarse ante disparadores aparentemente inocentes:</p>
    <ul>
      <li>Un dolor en un pecho o axila que hace pensar en recidiva.</li>
      <li>Estar cerca del aniversario del diagnóstico.</li>
      <li>Estar en el trayecto al hospital, aunque sea para una revisión rutinaria.</li>
      <li>Una noticia oncológica sobre alguien conocido.</li>
      <li>A veces, ningún disparador identificable — el sistema descarga porque llevaba tiempo cargado.</li>
    </ul>

    <h2>Ataque de pánico vs. urgencia médica: cómo distinguirlos</h2>
    <p>Esta duda es lo que <strong>más miedo añade</strong> a las supervivientes: "¿y si esta vez sí es algo?".</p>

    <p><strong>Signos que sugieren ataque de pánico</strong>:</p>
    <ul>
      <li>Pico intenso que sube en minutos y luego <strong>baja completamente</strong> en menos de 30 min.</li>
      <li>Contexto reconocible (estrés previo, mala noche, discusión, aniversario).</li>
      <li>Síntomas se calman con respiración lenta.</li>
      <li>Ya te ha pasado antes con la misma coreografía.</li>
    </ul>

    <p><strong>Signos que exigen llamar al 112 / ir a urgencias</strong>:</p>
    <ul>
      <li>Dolor torácico que se irradia a brazo izquierdo, mandíbula o espalda.</li>
      <li>Síntomas que <strong>no bajan</strong> tras 30-40 min.</li>
      <li>Pérdida de consciencia o desmayos reales.</li>
      <li>Debilidad brusca en un lado del cuerpo, alteración del habla.</li>
      <li>Sangrado, fiebre alta u otros signos claros distintos de ansiedad.</li>
    </ul>

    <p><strong>Regla práctica</strong>: si tienes dudas y llevas menos de 12 meses del alta, o si algo dentro de ti dice "esto no es como los otros", <em>ve a urgencias</em>. Ningún profesional te va a juzgar. Tu tranquilidad clínica es parte del proceso.</p>

    <h2>Qué hacer durante un ataque de pánico</h2>

    <h3>Los primeros 60 segundos</h3>
    <ol>
      <li><strong>Nombra lo que está pasando</strong>: "esto es un ataque de pánico. Va a durar unos minutos. No me voy a morir. No me voy a volver loca. Ya me ha pasado antes".</li>
      <li><strong>Si puedes, siéntate o apóyate</strong> en algo estable.</li>
      <li><strong>Exhalación alargada 4-6 (o mejor 4-8)</strong>: inhala por la nariz 4 segundos, exhala por la boca 6-8 segundos. El foco está en la exhalación larga — es lo que activa el nervio vago y desactiva el simpático.</li>
    </ol>

    <h3>De 1 a 10 minutos</h3>
    <ol>
      <li><strong>Anclaje sensorial 5-4-3-2-1</strong>: nombra 5 cosas que ves, 4 que oyes, 3 que puedes tocar, 2 que hueles, 1 que notas en el cuerpo.</li>
      <li><strong>Refresca cara y muñecas</strong> con agua fría si tienes cerca: activa el reflejo del buceo mamífero, que baja rápidamente frecuencia cardíaca.</li>
      <li><strong>No huyas</strong>: si el ataque te dio en un sitio (supermercado, coche, restaurante), quedarte hasta que baje evita que el sistema aprenda "este sitio es peligroso" y lo evite eternamente. Esto se llama "no reforzar la evitación".</li>
    </ol>

    <h3>Después del ataque</h3>
    <ul>
      <li>Agua, algo de azúcar, calor si tienes frío.</li>
      <li>Anota lo ocurrido: hora, contexto, disparador si lo identificas, duración, síntomas. Este registro es <strong>oro</strong> para trabajar con psicooncología.</li>
      <li>No te pases el resto del día pendiente de "si vuelve". El sistema, tras un ataque, entra en periodo refractario.</li>
    </ul>

    <h2>Cuándo pedir ayuda profesional (sin dudarlo)</h2>
    <ul>
      <li>Si tienes <strong>2 o más ataques</strong> de pánico en un mes.</li>
      <li>Si empiezas a <strong>evitar sitios o situaciones</strong> por miedo a que vuelvan.</li>
      <li>Si el miedo a tener otro ataque te <strong>limita la vida</strong>.</li>
      <li>Si aparecen ataques ligados específicamente a revisiones o pruebas.</li>
    </ul>
    <p>La combinación más eficaz según la evidencia: <strong>psicoterapia cognitivo-conductual + trabajo cuerpo-mente</strong> (yoga, mindfulness, respiración). En algunos casos, medicación ansiolítica temporal es útil como puente. Habla con tu médico o psicooncóloga.</p>

    <h2>Lecturas relacionadas</h2>
    <ul>
      <li><a href="/blog/ansiedad-despues-cancer">Ansiedad después del cáncer: por qué persiste y cómo trabajar con ella</a></li>
      <li><a href="/blog/ansiedad-hormonoterapia">Ansiedad y hormonoterapia: efectos secundarios psíquicos del tratamiento adyuvante</a></li>
      <li><a href="/blog/autocompasion-cancer">Autocompasión cuando el cuerpo falla</a></li>
    </ul>

    <p>Si convives con ataques de pánico post-oncológicos y necesitas herramientas cuerpo-mente concretas, la <a href="/sesion-gratuita">primera sesión conmigo es gratuita</a>.</p>
    <p>— Marta Montardit</p>
  </div>
);

const BodyCa = () => (
  <div className="article-body">
    <p>Ets al supermercat. Al mig del passadís de les fruites. Sense avís previ, sents que el cor s'accelera de cop, l'aire no t'arriba bé, se't nubla la vista. Penses: <em>"m'està passant alguna cosa. És una recidiva? És una embòlia? M'està venint un infart?"</em>. Surts corrents. Tres dies després et torna a passar al cotxe.</p>
    <p>Benvinguda al món dels <strong>atacs de pànic després del càncer</strong>. No t'estàs tornant boja, no estàs recaient i no t'estàs morint. Estàs tenint una resposta específica del teu sistema nerviós després d'un trauma mèdic sostingut.</p>

    <h2>Què és un atac de pànic (i què no és)</h2>
    <p>Un atac de pànic és una <strong>resposta aguda del sistema nerviós simpàtic</strong> — el mateix que s'activa si t'assalta un lleó — només que sense lleó visible. Dura entre 5 i 30 minuts, amb un pic intens al voltant del minut 10. És <strong>físicament molt real</strong> però clínicament inofensiu: no fa mal al teu cos encara que el sentis com una catàstrofe.</p>
    <p>Símptomes típics:</p>
    <ul>
      <li>Taquicàrdia i palpitacions.</li>
      <li>Sensació de falta d'aire, opressió al pit.</li>
      <li>Marejos, visió borrosa o "estranya".</li>
      <li>Sudoració, esgarrifances o calor.</li>
      <li>Formigueig a mans o cara.</li>
      <li>Sensació d'irrealitat (despersonalització).</li>
      <li>Por a "estar morint" o "tornar-se boja".</li>
    </ul>
    <p>I el més important: <strong>sempre baixa</strong>. Mai hi ha hagut un atac de pànic que no acabi. El sistema nerviós no pot mantenir aquest pic més de 30 min.</p>

    <h2>Per què apareixen a supervivents de càncer</h2>
    <p>Després d'un procés oncològic, el teu sistema nerviós ha viscut mesos o anys d'amenaça sostinguda (diagnòstic, tractament, proves, incertesa). Encara que mèdicament ja estiguis "lliure de malaltia", el teu sistema nerviós <strong>guarda memòria</strong> d'aquesta amenaça i pot activar-se davant disparadors aparentment innocents:</p>
    <ul>
      <li>Un dolor a un pit o axil·la que fa pensar en recidiva.</li>
      <li>Estar a prop de l'aniversari del diagnòstic.</li>
      <li>Estar al trajecte a l'hospital, encara que sigui per a una revisió rutinària.</li>
      <li>Una notícia oncològica sobre algú conegut.</li>
      <li>De vegades, cap disparador identificable — el sistema descarrega perquè feia temps que estava carregat.</li>
    </ul>

    <h2>Atac de pànic vs. urgència mèdica: com distingir-los</h2>
    <p>Aquest dubte és el que <strong>més por afegeix</strong> a les supervivents: "i si aquesta vegada sí que és alguna cosa?".</p>

    <p><strong>Senyals que suggereixen atac de pànic</strong>:</p>
    <ul>
      <li>Pic intens que puja en minuts i després <strong>baixa completament</strong> en menys de 30 min.</li>
      <li>Context reconeixible (estrès previ, mala nit, discussió, aniversari).</li>
      <li>Símptomes es calmen amb respiració lenta.</li>
      <li>Ja t'ha passat abans amb la mateixa coreografia.</li>
    </ul>

    <p><strong>Senyals que exigeixen trucar al 112 / anar a urgències</strong>:</p>
    <ul>
      <li>Dolor toràcic que s'irradia a braç esquerre, mandíbula o esquena.</li>
      <li>Símptomes que <strong>no baixen</strong> després de 30-40 min.</li>
      <li>Pèrdua de consciència o desmais reals.</li>
      <li>Debilitat brusca en un costat del cos, alteració de la parla.</li>
      <li>Sagnat, febre alta o altres senyals clars diferents d'ansietat.</li>
    </ul>

    <p><strong>Regla pràctica</strong>: si tens dubtes i portes menys de 12 mesos de l'alta, o si alguna cosa dins teu diu "això no és com els altres", <em>ves a urgències</em>. Cap professional et jutjarà. La teva tranquil·litat clínica és part del procés.</p>

    <h2>Què fer durant un atac de pànic</h2>

    <h3>Els primers 60 segons</h3>
    <ol>
      <li><strong>Anomena el que està passant</strong>: "això és un atac de pànic. Durarà uns minuts. No em moriré. No em tornaré boja. Ja m'ha passat abans".</li>
      <li><strong>Si pots, seu o recolza't</strong> en alguna cosa estable.</li>
      <li><strong>Exhalació allargada 4-6 (o millor 4-8)</strong>: inhala pel nas 4 segons, exhala per la boca 6-8 segons. El focus està en l'exhalació llarga — és el que activa el nervi vague i desactiva el simpàtic.</li>
    </ol>

    <h3>D'1 a 10 minuts</h3>
    <ol>
      <li><strong>Ancoratge sensorial 5-4-3-2-1</strong>: anomena 5 coses que veus, 4 que sents, 3 que pots tocar, 2 que fas olor, 1 que notes al cos.</li>
      <li><strong>Refresca cara i canells</strong> amb aigua freda si tens a prop: activa el reflex de busseig mamífer, que baixa ràpidament la freqüència cardíaca.</li>
      <li><strong>No fugis</strong>: si l'atac t'ha passat en un lloc (supermercat, cotxe, restaurant), quedar-te fins que baixi evita que el sistema aprengui "aquest lloc és perillós" i l'eviti eternament. Això s'anomena "no reforçar l'evitació".</li>
    </ol>

    <h3>Després de l'atac</h3>
    <ul>
      <li>Aigua, alguna cosa de sucre, calor si tens fred.</li>
      <li>Anota el que ha passat: hora, context, disparador si l'identifiques, durada, símptomes. Aquest registre és <strong>or</strong> per treballar amb psicooncologia.</li>
      <li>No et passis la resta del dia pendent de "si torna". El sistema, després d'un atac, entra en període refractari.</li>
    </ul>

    <h2>Quan demanar ajuda professional (sense dubtar-ho)</h2>
    <ul>
      <li>Si tens <strong>2 o més atacs</strong> de pànic en un mes.</li>
      <li>Si comences a <strong>evitar llocs o situacions</strong> per por que tornin.</li>
      <li>Si la por a tenir un altre atac et <strong>limita la vida</strong>.</li>
      <li>Si apareixen atacs lligats específicament a revisions o proves.</li>
    </ul>
    <p>La combinació més eficaç segons l'evidència: <strong>psicoteràpia cognitivo-conductual + treball cos-ment</strong> (ioga, mindfulness, respiració). En alguns casos, medicació ansiolítica temporal és útil com a pont. Parla amb el teu metge o psicooncòloga.</p>

    <h2>Lectures relacionades</h2>
    <ul>
      <li><a href="/blog/ansiedad-despues-cancer">Ansietat després del càncer: per què persisteix i com treballar-la</a></li>
      <li><a href="/blog/ansiedad-hormonoterapia">Ansietat i hormonoteràpia: efectes secundaris psíquics del tractament adjuvant</a></li>
      <li><a href="/blog/autocompasion-cancer">Autocompassió quan el cos falla</a></li>
    </ul>

    <p>Si convius amb atacs de pànic post-oncològics i necessites eines cos-ment concretes, la <a href="/sesion-gratuita">primera sessió amb mi és gratuïta</a>.</p>
    <p>— Marta Montardit</p>
  </div>
);

const ArticleAtaquesPanicoDespuesCancer = () => (
  <ArticleShell
    bodyEs={<BodyEs />}
    bodyCa={<BodyCa />}
    leadMagnet={{
      guide: "post-cancer",
      source: "blog/ataques-panico-despues-cancer",
      title: {
        es: "Guía: Volver a habitar el cuerpo después del cáncer",
        ca: "Guia: Tornar a habitar el cos després del càncer",
      },
      subtitle: {
        es: "Prácticas cuerpo-mente para las semanas y meses tras el tratamiento.",
        ca: "Pràctiques cos-ment per a les setmanes i mesos després del tractament.",
      },
      bullets: {
        es: [
          "Práctica para desactivar el sistema simpático en un ataque",
          "Anclaje 5-4-3-2-1 explicado paso a paso",
          "La recibes en tu email en menos de 5 minutos",
        ],
        ca: [
          "Pràctica per desactivar el sistema simpàtic en un atac",
          "Ancoratge 5-4-3-2-1 explicat pas a pas",
          "La reps al teu email en menys de 5 minuts",
        ],
      },
    }}
  />
);

export default ArticleAtaquesPanicoDespuesCancer;
